import {downloadFile} from '@domain/file-download'
import {buttonOnBlueSx, DateRangePicker} from '@hconnect/uikit/src/lib2'
import {
  defaultQuickSelectionItems,
  localDateTimeRangeToTimeRangeParam,
  timeRangeParamToDateRange
} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/dateRangePickerHelpers'
import {spawnErrorToast} from '@hm/fundamental'
import {FileDownload} from '@mui/icons-material'
import {LoadingButton} from '@mui/lab'
import {useMutation} from '@tanstack/react-query'
import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {downloadComparativeAnalysisExcel} from '../../common/backend'
import {FEATURE_NAMESPACE} from '../../consts'
import {DEFAULT_TIME_ZONE} from '../../utils'

type HierarchyExporterProps = {
  hierarchyIds: string[]
}

const formatDateForExport = (date: Date): string => {
  return date.toISOString().split('T')[0]
}

const getExportDates = ({
  exportFromDate,
  exportToDate,
  currentYear,
  today
}: {
  exportFromDate?: Date | null
  exportToDate?: Date | null
  currentYear: number
  today: Date
}): {exportStartDate: string; exportEndDate: string} => {
  const exportStartDate = formatDateForExport(exportFromDate ?? new Date(currentYear, 0, 1))
  const exportEndDate = formatDateForExport(exportToDate ?? today)

  return {exportStartDate, exportEndDate}
}

const isExportActionDisabled = ({
  hierarchyIds,
  isExportPending
}: {
  hierarchyIds: string[]
  isExportPending: boolean
}): boolean => {
  return hierarchyIds.length === 0 || isExportPending
}

export const HierarchyExporter: React.FC<HierarchyExporterProps> = ({hierarchyIds}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const today = useMemo(() => new Date(), [])
  const currentYear = today.getFullYear()
  const defaultExportTimeRange = useMemo(
    () =>
      localDateTimeRangeToTimeRangeParam([new Date(currentYear, 0, 1), today], DEFAULT_TIME_ZONE),
    [currentYear, today]
  )
  const [exportTimeRange, setExportTimeRange] = useState<string | undefined>(defaultExportTimeRange)

  const exportSelectionItems = useMemo(
    () => defaultQuickSelectionItems(t, DEFAULT_TIME_ZONE, today),
    [t, today]
  )

  const [exportFromDate, exportToDate] = useMemo(
    () =>
      timeRangeParamToDateRange({
        timeRange: exportTimeRange,
        selectionItems: exportSelectionItems
      }),
    [exportSelectionItems, exportTimeRange]
  )

  const {exportStartDate, exportEndDate} = getExportDates({
    exportFromDate,
    exportToDate,
    currentYear,
    today
  })

  const {isPending: isExportPending, mutate: exportComparativeAnalysis} = useMutation({
    mutationFn: () =>
      downloadComparativeAnalysisExcel({
        hierarchyIds,
        startDate: exportStartDate,
        endDate: exportEndDate
      }),
    onSuccess: (blob: Blob) => {
      downloadFile(blob, `easyPom-DefaultExport_${exportStartDate}_${exportEndDate}.xlsx`)
    },
    onError: () => {
      spawnErrorToast(t('errors.excelDownloadFailed'))
    }
  })

  const exportDisabled = isExportActionDisabled({hierarchyIds, isExportPending})

  return (
    <>
      <DateRangePicker
        onTimeRangeParamChange={setExportTimeRange}
        timeRangeParam={exportTimeRange}
        singleDateSwitchLabel={t('quickSelection.pickSingleDate')}
        selectionItems={exportSelectionItems}
        timezone={DEFAULT_TIME_ZONE}
        isContrastMode={false}
        label={t('comparativeAnalysis.exportDateRangeLabel')}
      />
      <LoadingButton
        variant="contained"
        onClick={() => exportComparativeAnalysis()}
        startIcon={<FileDownload />}
        loading={isExportPending}
        loadingPosition="start"
        disabled={exportDisabled}
        sx={buttonOnBlueSx}
        data-cy="comparative-analysis-export-button"
      >
        {t('comparativeAnalysis.exportButton')}
      </LoadingButton>
    </>
  )
}
